import { createTheme, responsiveFontSizes, alpha } from '@mui/material/styles';
import {
  PrimaryColor,
  SecondaryColor,
  SemanticErrorColor,
  SemanticSuccessColor,
  SemanticWarningColor,
  SemanticInfoColor,
  GreenHighlightColor,
  PremiumColor,
  SludgeColor,
  ButterCupColor,
  ChristineColor,
  GreenyColor,
  HibiscusColor,
  PremiumAddUpColor,
  SmithAppleColor,
  NeutralsColor,
} from './color-palette/color-palette.theme';

import {Typography} from './typography/typography.theme';
import { Breakpoints, Spacing } from './theme-mislaneous/mislaneous.theme';

import '@mui/material/Typography';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    title: React.CSSProperties;
    highlight: React.CSSProperties;
    info: React.CSSProperties;
    caption1: React.CSSProperties;
    caption2: React.CSSProperties;
    label: React.CSSProperties;
    header: React.CSSProperties;
    sectionLabel: React.CSSProperties;
    cardTitle: React.CSSProperties;
    cardTitleSm: React.CSSProperties;
    unitSuffix: React.CSSProperties;
    metricValue: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    title?: React.CSSProperties;
    highlight?: React.CSSProperties;
    info?: React.CSSProperties;
    caption1?: React.CSSProperties;
    caption2?: React.CSSProperties;
    label?: React.CSSProperties;
    header?: React.CSSProperties;
    sectionLabel?: React.CSSProperties;
    cardTitle?: React.CSSProperties;
    cardTitleSm?: React.CSSProperties;
    unitSuffix?: React.CSSProperties;
    metricValue?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    title: true;
    highlight: true;
    info: true;
    caption1: true;
    caption2: true;
    label: true;
    header: true;
    sectionLabel: true;
    cardTitle: true;
    cardTitleSm: true;
    unitSuffix: true;
    metricValue: true;
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    Primary: Palette['primary'];
    Secondary: Palette['secondary'];
    SemanticError: Palette['error'];
    SemanticSuccess: Palette['success'];
    SemanticWarning: Palette['warning'];
    SemanticInfo: Palette['info'];
    Neutrals: Palette['grey'];
    GreenHighlight: Palette['primary'];
    Premium: Palette['primary'];
    Sludge: Palette['primary'];
    ButterCup: Palette['primary'];
    Christine: Palette['primary'];
    Greeny: Palette['primary'];
    Hibiscus: Palette['primary'];
    PremiumAddUp: Palette['primary'];
    SmithApple: Palette['primary'];
  }

  interface PaletteOptions {
    Primary: PaletteOptions['primary'];
    Secondary: PaletteOptions['secondary'];
    SemanticError: PaletteOptions['error'];
    SemanticSuccess: PaletteOptions['success'];
    SemanticWarning: PaletteOptions['warning'];
    SemanticInfo: PaletteOptions['info'];
    Neutrals: PaletteOptions['grey'];
    GreenHighlight: PaletteOptions['primary'];
    Premium: PaletteOptions['primary'];
    Sludge: PaletteOptions['primary'];
    ButterCup: PaletteOptions['primary'];
    Christine: PaletteOptions['primary'];
    Greeny: PaletteOptions['primary'];
    Hibiscus: PaletteOptions['primary'];
    PremiumAddUp: PaletteOptions['primary'];
    SmithApple: PaletteOptions['primary'];
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    Primary: true;
    Secondary: true;
    SemanticError: true;
    SemanticSuccess: true;
    SemanticWarning: true;
    SemanticInfo: true;
    Neutrals: true;
    GreenHighlight: true;
    Premium: true;
    Sludge: true;
    ButterCup: true;
    Christine: true;
    Greeny: true;
    Hibiscus: true;
    PremiumAddUp: true;
    SmithApple: true;
  }
}

declare module '@mui/material/Checkbox' {
  interface CheckboxPropsColorOverrides {
    Primary: true;
    Secondary: true;
    SemanticError: true;
    SemanticSuccess: true;
    SemanticWarning: true;
    SemanticInfo: true;
    Neutrals: true;
    GreenHighlight: true;
    Premium: true;
    Sludge: true;
    ButterCup: true;
    Christine: true;
    Greeny: true;
    Hibiscus: true;
    PremiumAddUp: true;
    SmithApple: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    Primary: true;
    Secondary: true;
    SemanticError: true;
    SemanticSuccess: true;
    SemanticWarning: true;
    SemanticInfo: true;
    Neutrals: true;
    GreenHighlight: true;
    Premium: true;
    Sludge: true;
    ButterCup: true;
    Christine: true;
    Greeny: true;
    Hibiscus: true;
    PremiumAddUp: true;
    SmithApple: true;
  }
}

declare module '@mui/material/TextField' {
  interface TextFieldPropsColorOverrides {
    Primary: true;
    Secondary: true;
    SemanticError: true;
    SemanticSuccess: true;
    SemanticWarning: true;
    SemanticInfo: true;
    Neutrals: true;
    GreenHighlight: true;
    Premium: true;
    Sludge: true;
    ButterCup: true;
    Christine: true;
    Greeny: true;
    Hibiscus: true;
    PremiumAddUp: true;
    SmithApple: true;
  }
}


declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    highlight: true;
    caption1: true;
    caption2: true;
    label: true;
    body: true;
    header: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    Primary: true;
    Secondary: true;
    SemanticError: true;
    SemanticSuccess: true;
    SemanticWarning: true;
    SemanticInfo: true;
    Neutrals: true;
    GreenHighlight: true;
    Premium: true;
    Sludge: true;
    ButterCup: true;
    Christine: true;
    Greeny: true;
    Hibiscus: true;
    PremiumAddUp: true;
    SmithApple: true;
  }
}

const Theme = responsiveFontSizes(
  createTheme({
    palette: {
      Primary: PrimaryColor,
      primary: PrimaryColor,      
      Secondary: SecondaryColor,
      SemanticError: SemanticErrorColor,
      SemanticSuccess: SemanticSuccessColor,
      SemanticWarning: SemanticWarningColor,
      SemanticInfo: SemanticInfoColor,
      Neutrals: NeutralsColor,
      GreenHighlight: GreenHighlightColor,
      Premium: PremiumColor,
      Sludge: SludgeColor,
      ButterCup: ButterCupColor,
      Christine: ChristineColor,
      Greeny: GreenyColor,
      Hibiscus: HibiscusColor,
      PremiumAddUp: PremiumAddUpColor,
      SmithApple: SmithAppleColor,
    },
    typography: Typography,
    breakpoints: Breakpoints,
    spacing: Spacing,
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: theme.shape.borderRadius * 2.5,
            boxShadow: 'none',
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            paddingLeft: theme.spacing(4),
            paddingRight: theme.spacing(4),
            '&:hover': { boxShadow: 'none' },
          }),
          sizeSmall: ({ theme }) => ({
            fontWeight: 600,
            borderRadius: theme.shape.borderRadius * 2,
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
          }),
          outlined: ({ theme }) => ({
            borderColor: theme.palette.divider,
            backgroundColor: 'transparent',
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius * 2.5,
            backgroundColor: theme.palette.Neutrals[50],
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.Neutrals[400],
            },
            '&.Mui-focused': {
              backgroundColor: theme.palette.background.paper,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.primary.main,
              borderWidth: 1,
            },
          }),
        },
      },
      MuiMenu: {
        styleOverrides: { paper: ({ theme }) => ({ borderRadius: theme.shape.borderRadius * 2.5 }) },
      },
      MuiChip: {
        styleOverrides: { root: ({ theme }) => ({ borderRadius: theme.shape.borderRadius * 2.25 }) },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: theme.shape.borderRadius * 4,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: 0,
            paddingTop: theme.spacing(4),
            gap: theme.spacing(2),
          }),
        },
      },
    },
  })
);


export default Theme;
export const FONT_NAME = '"Roboto", "Inter", sans-serif';